import { useAuth0 } from "@auth0/auth0-react";
import React, { useState, useEffect } from "react";
import { Stack } from "@mui/material";
import { PageLayout } from "../components/page-layout";
import {
  getBasicUserInfo,
  getContactData,
  getVerificationData,
  updateContactData,
  updateUserName,
  removeContactData,
  requestEmailChange
} from "../services/backend.service";
import { usePermission, usePermissions, useRole } from "../providers/PermissionsProvider";
import { PermissionGate } from "../components/PermissionGate";
import { PageLoader } from "../components/page-loader";
import { ProfileItemModal } from "../components/profile-item-modal";
import {
  Button,
  Eyebrow,
  Field,
  FormActions,
  OnyxBanner,
  PageHeader,
  Panel,
  SectionHeader,
  Tag,
  Text,
  TextInput,
} from "../components/onyx";

export const ProfilePage = () => {
  const { user, getAccessTokenSilently, logout } = useAuth0();
  const { can, loading: permissionsLoading } = usePermission();
  const { orgId, orgName, workspaceId, roles } = usePermissions();
  const { isMember, isCustomer, isAdmin } = useRole();
  
  // Disaggregated state - only load what user has permission for
  const [basicData, setBasicData] = useState(null);
  const [contactData, setContactData] = useState(null);
  const [verificationData, setVerificationData] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  
  const [emailEditMode, setEmailEditMode] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailChangeStatus, setEmailChangeStatus] = useState(null);
  const [emailChangeLoading, setEmailChangeLoading] = useState(false);
  
  const isDbUser = user?.sub?.startsWith('auth0|');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await getAccessTokenSilently();
        
        // Always load basic info (no permission needed)
        const fetchPromises = [getBasicUserInfo(token)];
        const dataTypes = ['basic'];
        
        // Conditionally load based on permissions
        if (can('contact:read')) {
          fetchPromises.push(getContactData(token));
          dataTypes.push('contact');
        }
        
        if (can('verification:read')) {
          fetchPromises.push(getVerificationData(token));
          dataTypes.push('verification');
        }
        
        const responses = await Promise.all(fetchPromises);
        
        // Map responses to state
        responses.forEach((response, index) => {
          const type = dataTypes[index];
          if (response.error) {
            console.error(`Failed to load ${type}:`, response.error);
            return;
          }
          
          switch(type) {
            case 'basic':
              setBasicData(response.data);
              break;
            case 'contact':
              setContactData(response.data);
              break;
            case 'verification':
              setVerificationData(response.data);
              break;
            default:
              break;
          }
        });
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch when user is ready and permissions are loaded
    if (user && !permissionsLoading) {
      fetchData();
    }
  }, [user, getAccessTokenSilently, permissionsLoading]);  // Removed 'can' - it's a function!

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Remove all non-digits
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Check if it's a 10-digit US number
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    
    // Check if it's an 11-digit number starting with 1
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    
    // Return original if it doesn't match expected formats
    return phoneNumber;
  };

  const handleAddItem = (type) => {
    setModalType(type);
    setModalOpen(true);
  };

  const handleRemoveItem = async (type, id) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await removeContactData(token, type, id);
      if (response.error) throw response.error;
      
      // Refresh contact data after removal
      const contactResponse = await getContactData(token);
      if (!contactResponse.error) {
        setContactData(contactResponse.data);
      }
    } catch (err) {
      setError(err);
    }
  };

  const handleSaveItem = async (data) => {
    try {
      const token = await getAccessTokenSilently();
      let payload;
      let isNameUpdate = false;
      
      switch (modalType) {
        case 'phone':
          payload = { phone: data.number };
          break;
        case 'address':
          payload = { 
            address: {
              address1: data.street1,
              address2: data.street2,
              city: data.city,
              state: data.state,
              zip: data.zipCode
            }
          };
          break;
        case 'alias':
          payload = { 
            alias: {
              first_name: data.firstName,
              last_name: data.lastName,
              type: data.type,
              prefix: data.prefix,
              middle_names: data.middleNames,
              suffix: data.suffix
            }
          };
          break;
        case 'name':
          payload = {
            first_name: data.firstName,
            last_name: data.lastName
          };
          isNameUpdate = true;
          break;
      }
      
      // Use appropriate endpoint based on type
      if (isNameUpdate) {
        const response = await updateUserName(token, payload);
        if (response.error) throw response.error;
        
        // Refresh basic info
        const basicResponse = await getBasicUserInfo(token);
        if (!basicResponse.error) {
          setBasicData(basicResponse.data);
        }
      } else {
        const response = await updateContactData(token, payload);
        if (response.error) throw response.error;
        
        // Refresh contact data
        const contactResponse = await getContactData(token);
        if (!contactResponse.error) {
          setContactData(contactResponse.data);
        }
      }
      
      setModalOpen(false);
    } catch (err) {
      setError(err);
    }
  };

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <PageLayout>
        <PageLoader />
      </PageLayout>
    );
  }

  const handleEmailChange = async () => {
    if (!newEmail.trim()) return;
    try {
      setEmailChangeLoading(true);
      setEmailChangeStatus(null);
      const token = await getAccessTokenSilently();
      const { error: apiError } = await requestEmailChange(token, newEmail.trim());
      if (apiError) {
        setEmailChangeStatus({ type: 'error', message: apiError.message || 'Failed to send verification email' });
      } else {
        setEmailChangeStatus({ type: 'success', message: 'Check your new email for a verification link' });
        setEmailEditMode(false);
      }
    } catch (err) {
      setEmailChangeStatus({ type: 'error', message: 'An unexpected error occurred' });
    } finally {
      setEmailChangeLoading(false);
    }
  };

  const handlePasswordReset = () => {
    const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN;
    window.open(`https://${auth0Domain}/u/reset-password`, '_blank');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateWithoutTimezone = (dateString) => {
    if (!dateString) return 'N/A';
    // Parse the date string directly without creating a Date object to avoid timezone conversion
    const [year, month, day] = dateString.split('-');
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
  };

  return (
    <PageLayout>
      <div className="content-layout content-layout--reading">
        <PageHeader title="Profile" />

        <Panel>
          <SectionHeader
            as="h2"
            title={`${basicData?.first_name || ''} ${basicData?.last_name || ''}`.trim()}
            actions={(
              <Button
                variant="secondary"
                size="dense"
                onClick={() => handleAddItem('name')}
              >
                Edit Name
              </Button>
            )}
          />
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: emailEditMode || emailChangeStatus ? 2 : 0 }}
          >
            <Text variant="body-m" as="span">{basicData?.email}</Text>
            {isDbUser && !emailEditMode ? (
              <Button
                variant="secondary"
                size="dense"
                onClick={() => {
                  setEmailEditMode(true);
                  setNewEmail('');
                  setEmailChangeStatus(null);
                }}
              >
                Change Email
              </Button>
            ) : null}
            {!isDbUser ? (
              <Text variant="body-s" as="span">
                Managed by your {user?.sub?.startsWith('google-oauth2|') ? 'Google' : 'social'} account
              </Text>
            ) : null}
          </Stack>
          {emailEditMode ? (
            <div data-testid="profile-email-edit">
              <Field label="New Email" htmlFor="profile-email-input">
                <TextInput
                  id="profile-email-input"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="New email address"
                  autoComplete="email"
                />
              </Field>
              <FormActions>
                <Button
                  variant="primary"
                  size="dense"
                  chevron={false}
                  onClick={handleEmailChange}
                  disabled={emailChangeLoading || !newEmail.trim()}
                >
                  {emailChangeLoading ? 'Sending...' : 'Send Verification'}
                </Button>
                <Button
                  variant="secondary"
                  size="dense"
                  onClick={() => {
                    setEmailEditMode(false);
                    setEmailChangeStatus(null);
                  }}
                >
                  Cancel
                </Button>
              </FormActions>
            </div>
          ) : null}
          {emailChangeStatus ? (
            <OnyxBanner
              severity={emailChangeStatus.type === 'error' ? 'error' : 'success'}
            >
              {emailChangeStatus.message}
            </OnyxBanner>
          ) : null}
        </Panel>

        <PermissionGate require="chat:access">
          <Panel>
            <SectionHeader title="Organization Information" as="h3" />
            <div className="onyx-detail-list">
              <div className="onyx-detail-row">
                <span className="onyx-detail-label">Organization</span>
                <span className="onyx-detail-value">
                  {orgName || orgId || 'Not assigned'}
                </span>
              </div>
              <div className="onyx-detail-row">
                <span className="onyx-detail-label">Role</span>
                <span className="onyx-detail-value">
                  {isAdmin ? 'Administrator' :
                   roles.includes('org_admin') ? 'Team Admin' :
                   roles.includes('org_user') ? 'Researcher' :
                   isMember ? 'Member' :
                   'Not Assigned'}
                </span>
              </div>
            </div>
          </Panel>
        </PermissionGate>

        <PermissionGate require="contact:read">
          <Panel>
            <SectionHeader
              title="Contact Information History"
              description="This will help us in obtaining medical records for our studies"
              as="h3"
            />

            <Stack spacing={3}>
              <div>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                  <Eyebrow>Address History</Eyebrow>
                  <Button
                    variant="secondary"
                    size="dense"
                    onClick={() => handleAddItem('address')}
                  >
                    Add Address
                  </Button>
                </Stack>
                {contactData?.addresses?.length ? (
                  <div className="onyx-detail-list">
                    {contactData.addresses.map((address, index) => (
                      <div key={index} className="onyx-detail-row onyx-detail-row--address">
                        <span className="onyx-detail-value">
                          {address?.address?.address1}
                          {address?.address?.address2 && `, ${address?.address?.address2}`}
                          <br />
                          {address?.address?.city}, {address?.address?.state} {address?.address?.zip}
                        </span>
                        <Button
                          variant="ghost"
                          size="dense"
                          onClick={() => handleRemoveItem('address', address.id)}
                          aria-label="Remove address"
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Text variant="body-s">No addresses yet</Text>
                )}
              </div>

              <div>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                  <Eyebrow>Phone Number History</Eyebrow>
                  <Button
                    variant="secondary"
                    size="dense"
                    onClick={() => handleAddItem('phone')}
                  >
                    Add Phone
                  </Button>
                </Stack>
                {contactData?.phone_numbers?.length ? (
                  <div className="onyx-detail-list">
                    {contactData.phone_numbers.map((phone, index) => (
                      <div key={index} className="onyx-detail-row">
                        <span className="onyx-detail-value">
                          {formatPhoneNumber(phone?.phone_number?.phone_number)}
                        </span>
                        <Button
                          variant="ghost"
                          size="dense"
                          onClick={() => handleRemoveItem('phone_number', phone.id)}
                          aria-label="Remove phone number"
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Text variant="body-s">No phone numbers yet</Text>
                )}
              </div>

              <div>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                  <Eyebrow>Other Names Used</Eyebrow>
                  <Button
                    variant="secondary"
                    size="dense"
                    onClick={() => handleAddItem('alias')}
                  >
                    Add Name
                  </Button>
                </Stack>
                {contactData?.aliases?.length ? (
                  <div className="onyx-detail-list">
                    {contactData.aliases.map((alias, index) => (
                      <div key={index} className="onyx-detail-row">
                        <span className="onyx-detail-value">
                          {alias?.alias?.prefix} {alias?.alias?.first_name} {alias?.alias?.middle_names} {alias?.alias?.last_name} {alias?.alias?.suffix}
                        </span>
                        <Button
                          variant="ghost"
                          size="dense"
                          onClick={() => handleRemoveItem('alias', alias.id)}
                          aria-label="Remove alias"
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Text variant="body-s">No other names yet</Text>
                )}
              </div>
            </Stack>
          </Panel>
        </PermissionGate>

        <PermissionGate require="verification:read">
          {verificationData?.verification_data ? (
            <Panel>
              <SectionHeader title="Identity Verification (From ID Card)" as="h3" />
              <div className="onyx-detail-list">
                <div className="onyx-detail-row">
                  <span className="onyx-detail-label">Status</span>
                  <span className="onyx-detail-value">
                    {verificationData.verification_data.status === 'COMPLETED' ? (
                      <Tag variant="success">Verified</Tag>
                    ) : (
                      <Tag variant="muted">{verificationData.verification_data.status}</Tag>
                    )}
                  </span>
                </div>
                <div className="onyx-detail-row">
                  <span className="onyx-detail-label">Verified On</span>
                  <span className="onyx-detail-value">
                    {formatDate(verificationData.verification_data.completed_at)}
                  </span>
                </div>
              </div>

              <div className="onyx-detail-group">
                <Eyebrow>Government ID</Eyebrow>
                <div className="onyx-detail-list">
                  <div className="onyx-detail-row">
                    <span className="onyx-detail-label">ID Type</span>
                    <span className="onyx-detail-value">
                      {verificationData.verification_data.id_type?.toUpperCase()}
                    </span>
                  </div>
                  <div className="onyx-detail-row">
                    <span className="onyx-detail-label">State</span>
                    <span className="onyx-detail-value">
                      {verificationData.verification_data.id_state}
                    </span>
                  </div>
                  <div className="onyx-detail-row">
                    <span className="onyx-detail-label">Date of Birth</span>
                    <span className="onyx-detail-value">
                      {formatDateWithoutTimezone(verificationData.verification_data.date_of_birth)}
                    </span>
                  </div>
                  <div className="onyx-detail-row">
                    <span className="onyx-detail-label">Expiration</span>
                    <span className="onyx-detail-value">
                      {formatDateWithoutTimezone(verificationData.verification_data.id_expiration_date)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="onyx-detail-group">
                <Eyebrow>Verified Address</Eyebrow>
                <Text variant="body-m">
                  {verificationData.verification_data.address_street_1}
                  {verificationData.verification_data.address_street_2 && `, ${verificationData.verification_data.address_street_2}`}
                  <br />
                  {verificationData.verification_data.address_city}, {verificationData.verification_data.address_state} {verificationData.verification_data.address_postal_code}
                </Text>
              </div>
            </Panel>
          ) : null}
        </PermissionGate>
      </div>

      <ProfileItemModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveItem}
        type={modalType}
        initialData={modalType === 'name' ? {
          firstName: basicData?.first_name || '',
          lastName: basicData?.last_name || ''
        } : null}
      />
    </PageLayout>
  );
};
